import { type KeyboardEvent, type MouseEvent, type PointerEvent, useRef } from 'react';

interface LightweightSliderProps {
  currentValue: number;
  max: number;
  min?: number;
  step?: number;
  disabled?: boolean;
  onChange: (value: number) => void;
  onMouseUp?: () => void;
}

const THUMB_SIZE = 20;
const THUMB_RADIUS = THUMB_SIZE / 2;

// 3D playback scrubbers update on every playback frame. The Radix slider is
// still the default shared component, but it allocates too much for this hot path.
export default function LightweightSlider({
  currentValue,
  disabled = false,
  max,
  min = 0,
  onChange,
  onMouseUp,
  step = 1,
}: LightweightSliderProps) {
  const lastEmittedValueRef = useRef(currentValue);
  const draggingPointerIdRef = useRef<number | null>(null);
  const normalizedValue = normalizeSliderValue(currentValue, min, max, step);
  const percentage = getSliderPercentage(normalizedValue, min, max);
  const sliderPosition = getSliderPosition(percentage);

  lastEmittedValueRef.current = normalizedValue;

  const emitValue = (nextValue: number) => {
    const normalizedNextValue = normalizeSliderValue(nextValue, min, max, step);
    if (normalizedNextValue === lastEmittedValueRef.current) {
      return;
    }

    lastEmittedValueRef.current = normalizedNextValue;
    onChange(normalizedNextValue);
  };

  const updateFromPointer = (event: PointerEvent<HTMLDivElement>) => {
    emitValue(getSliderValueFromPointer(event, min, max, step));
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (disabled || event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    draggingPointerIdRef.current = event.pointerId;
    updateFromPointer(event);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (disabled || draggingPointerIdRef.current !== event.pointerId) {
      return;
    }

    updateFromPointer(event);
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (draggingPointerIdRef.current !== event.pointerId) {
      return;
    }

    draggingPointerIdRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    onMouseUp?.();
  };

  const handlePointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    if (draggingPointerIdRef.current !== event.pointerId) {
      return;
    }

    draggingPointerIdRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        event.preventDefault();
        emitValue(normalizedValue - step);
        return;
      case 'ArrowRight':
      case 'ArrowUp':
        event.preventDefault();
        emitValue(normalizedValue + step);
        return;
      case 'Home':
        event.preventDefault();
        emitValue(min);
        return;
      case 'End':
        event.preventDefault();
        emitValue(max);
        return;
    }
  };

  const handleLostPointerCapture = (event: PointerEvent<HTMLDivElement>) => {
    if (draggingPointerIdRef.current === event.pointerId) {
      draggingPointerIdRef.current = null;
    }
  };

  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  return (
    <div
      className="relative flex h-5 w-full touch-none items-center"
      onContextMenu={handleContextMenu}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onLostPointerCapture={handleLostPointerCapture}
    >
      <div className="bg-gray-30 dark:bg-gray-60 h-[5px] w-full rounded-full">
        <div
          className="bg-blue h-full rounded-full"
          style={{ width: sliderPosition }}
        />
      </div>
      {!disabled ? (
        <div
          role="slider"
          tabIndex={0}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={normalizedValue}
          aria-disabled={disabled}
          className="border-blue dark:bg-gray-90 absolute top-1/2 z-30 block h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] bg-white transition-colors hover:dark:border-white"
          style={{ left: sliderPosition }}
          onKeyDown={handleKeyDown}
        />
      ) : null}
    </div>
  );
}

function getSliderValueFromPointer(
  event: PointerEvent<HTMLDivElement>,
  min: number,
  max: number,
  step: number,
) {
  const rect = event.currentTarget.getBoundingClientRect();
  const minPointerX = Math.min(THUMB_RADIUS, rect.width);
  const maxPointerX = Math.max(rect.width - THUMB_RADIUS, minPointerX);
  const pointerX = Math.min(
    Math.max(event.clientX - rect.left, minPointerX),
    maxPointerX,
  );
  const usableWidth = Math.max(maxPointerX - minPointerX, 1);
  const percentage = (pointerX - minPointerX) / usableWidth;

  return normalizeSliderValue(min + (max - min) * percentage, min, max, step);
}

function normalizeSliderValue(
  value: number,
  min: number,
  max: number,
  step: number,
) {
  const normalizedMin = Math.min(min, max);
  const normalizedMax = Math.max(min, max);
  const normalizedStep = Number.isFinite(step) && step > 0 ? step : 1;
  const clampedValue = Math.min(Math.max(value, normalizedMin), normalizedMax);
  const steppedValue =
    Math.round((clampedValue - normalizedMin) / normalizedStep) *
      normalizedStep +
    normalizedMin;

  const decimalPlaces = getDecimalPlaces(normalizedStep);

  return Math.min(
    Math.max(Number(steppedValue.toFixed(decimalPlaces)), normalizedMin),
    normalizedMax,
  );
}

function getDecimalPlaces(value: number) {
  const decimalValue = value.toString().split('.')[1];

  return decimalValue?.length ?? 0;
}

function getSliderPercentage(value: number, min: number, max: number) {
  if (max <= min) {
    return 0;
  }

  return ((value - min) / (max - min)) * 100;
}

function getSliderPosition(percentage: number) {
  const normalizedPercentage = Math.min(Math.max(percentage, 0), 100);
  const insetOffset = THUMB_RADIUS - (normalizedPercentage / 100) * THUMB_SIZE;

  return `calc(${normalizedPercentage}% + ${insetOffset}px)`;
}
